/**
 * Alta de presupuesto.
 *
 * Un presupuesto es el único documento de venta que **se puede rehacer
 * entero** mientras esté en borrador: no tiene efectos fiscales y nadie lo
 * ha aceptado todavía. Por eso aquí sí hay un editor de líneas completo,
 * mientras que en una factura emitida no lo habrá.
 *
 * La validez sí importa: un presupuesto sin fecha de caducidad se acepta
 * dos años después al precio de hace dos años.
 */

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from '../../forms/use-form.js';
import { FormSection, TextArea, TextField } from '../../forms/fields.js';
import { EntityPicker } from '../../forms/entity-picker.js';
import { LineEditor, TotalsSummary, emptyLine, useLineTotals, type EditableLine } from '../../forms/line-editor.js';
import { isoDate, type FieldRules } from '../../forms/form-state.js';

const RULES: FieldRules = {
  validUntil: [isoDate()],
};

export function QuoteFormScreen(): React.ReactNode {
  const navigate = useNavigate();
  const counter = useRef(1);
  const nextId = (): string => `l${counter.current++}`;

  const [customerId, setCustomerId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [customerError, setCustomerError] = useState<string | undefined>(undefined);
  const [lines, setLines] = useState<EditableLine[]>([emptyLine(nextId())]);
  const [lineError, setLineError] = useState<string | null>(null);

  const form = useForm<{ id: string; number: string }>({
    mode: 'create',
    rules: RULES,
    nullable: ['validUntil', 'siteId', 'globalDiscountRate'],
    initial: {
      title: '',
      validUntil: '',
      globalDiscountRate: '',
      conditions: '',
    },
    onSubmit: async (body, client) =>
      client.post<{ id: string; number: string }>('/quotes', {
        ...body,
        customerId,
        siteId: siteId === '' ? null : siteId,
        lines: lines
          .filter((line) => line.description.trim() !== '')
          .map((line) => ({
            kind: line.kind,
            description: line.description.trim(),
            // Los importes viajan como cadena decimal: convertirlos a
            // `number` metería coma flotante en el camino del dinero.
            quantity: line.kind === 'TEXTO' ? '0' : line.quantity.replace(',', '.'),
            unitPrice: line.kind === 'TEXTO' ? '0.00' : line.unitPrice.replace(',', '.'),
            discountRate: line.discountRate.trim() === '' ? null : line.discountRate.replace(',', '.'),
            taxRate: line.kind === 'TEXTO' ? '0' : line.taxRate,
          })),
      }),
    onSaved: () => void navigate('/presupuestos'),
  });

  const totals = useLineTotals(lines, String(form.values['globalDiscountRate'] ?? ''));

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();

    if (customerId === '') {
      setCustomerError('Elija el cliente del presupuesto.');
      return;
    }
    setCustomerError(undefined);

    const useful = lines.filter((line) => line.description.trim() !== '');
    if (useful.length === 0) {
      setLineError('Un presupuesto necesita al menos una línea con concepto.');
      return;
    }
    if (totals.invalidLines.length > 0) {
      setLineError('Hay líneas con números que no se pueden interpretar. Revíselas antes de guardar.');
      return;
    }
    setLineError(null);

    await form.submit();
  }

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Nuevo presupuesto</h1>
        {form.dirty && <span className="screen__count">Cambios sin guardar</span>}
      </header>

      {(form.formError !== null || lineError !== null) && (
        <p className="screen__error" role="alert">
          {lineError ?? form.formError}
        </p>
      )}

      {form.orphanErrors.length > 0 && (
        <p className="screen__error" role="alert">
          El servidor ha rechazado campos que este formulario no muestra:{' '}
          {form.orphanErrors.join(' · ')}
        </p>
      )}

      <form className="form" onSubmit={(e) => void submit(e)} noValidate>
        <FormSection title="Destinatario">
          <EntityPicker
            label="Cliente"
            entity="CUSTOMER"
            value={customerId}
            onChange={(v) => {
              setCustomerId(v);
              setCustomerError(undefined);
            }}
            error={customerError}
            required
          />
          <EntityPicker
            label="Centro"
            entity="SITE"
            value={siteId}
            onChange={setSiteId}
            help="Si el presupuesto es para un centro concreto."
          />
          <TextField
            label="Título"
            value={String(form.values['title'] ?? '')}
            onChange={(v) => form.set('title', v)}
          />
          <TextField
            label="Válido hasta"
            type="date"
            value={String(form.values['validUntil'] ?? '')}
            onChange={(v) => form.set('validUntil', v)}
            onBlur={() => form.blur('validUntil')}
            error={form.errors['validUntil']}
            help="Sin fecha, un presupuesto se acepta dos años después al precio de hace dos años."
          />
        </FormSection>

        <FormSection title="Líneas">
          <div className="form__full">
            <LineEditor lines={lines} onChange={setLines} totals={totals} nextId={nextId} />
          </div>
          <TextField
            label="Descuento global (%)"
            type="number"
            step="0.01"
            value={String(form.values['globalDiscountRate'] ?? '')}
            onChange={(v) => form.set('globalDiscountRate', v)}
            help="Se aplica sobre el conjunto, después de los descuentos de línea."
          />
          <div className="form__full">
            <TotalsSummary totals={totals} />
          </div>
        </FormSection>

        <FormSection title="Condiciones">
          <TextArea
            label="Condiciones del presupuesto"
            value={String(form.values['conditions'] ?? '')}
            onChange={(v) => form.set('conditions', v)}
            rows={4}
            help="Lo que el cliente acepta al firmar: plazos, formas de pago, exclusiones."
          />
        </FormSection>

        <footer className="form__actions">
          <button className="button button--primary" type="submit" disabled={form.saving}>
            {form.saving ? 'Creando…' : 'Crear presupuesto'}
          </button>
          <button
            className="button button--quiet"
            type="button"
            onClick={() => {
              if (form.dirty && !window.confirm('Hay cambios sin guardar. ¿Salir igualmente?')) return;
              void navigate('/presupuestos');
            }}
          >
            Volver
          </button>
        </footer>
      </form>
    </section>
  );
}
